import { useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion'
import { useAuthStore } from '../stores/authStore'
import { useLocale } from '../hooks/useLocale'
import WelcomeStep from '../components/onboarding/WelcomeStep'
import VerifyEmailStep from '../components/onboarding/VerifyEmailStep'
import SecureIdentityStep from '../components/onboarding/SecureIdentityStep'
import NamespaceEducation from '../components/onboarding/NamespaceEducation'
import CompletionStep from '../components/onboarding/CompletionStep'

const brandFontStyle: React.CSSProperties = {
  fontFamily: '"Helvetica Neue", "HelveticaNeue-UltraLight", "Helvetica", Arial, sans-serif',
  fontWeight: 100,
}

export default function OnboardingPage() {
  const navigate = useNavigate()
  const { tDeep } = useLocale()
  const { onboarding, setOnboardingStep, isAuthenticated } = useAuthStore()
  const { step } = onboarding
  const prefersReducedMotion = useReducedMotion()

  const stepTitles = useMemo(() => [
    { main: 'Begin.', sub: 'Your identity starts with a single step.' },
    { main: 'Verify.', sub: 'Confirm you are who you say you are.' },
    { main: tDeep('onboarding', 'steps', 'step3', 'main'), sub: tDeep('onboarding', 'steps', 'step3', 'sub') },
    { main: tDeep('onboarding', 'steps', 'step4', 'main'), sub: tDeep('onboarding', 'steps', 'step4', 'sub') },
    { main: 'Welcome.', sub: 'You are now part of something greater.' },
  ], [tDeep])

  useEffect(() => {
    if (isAuthenticated && step === 1) {
      // Handle authenticated redirect if needed
    }
  }, [isAuthenticated, step, navigate])

  const handleNextStep = () => {
    setOnboardingStep(step + 1)
  }

  const currentTitle = stepTitles[step - 1] || stepTitles[0]

  return (
    <div className="min-h-screen flex text-white selection:bg-teal-400/20 selection:text-white">
      {/* Left Panel - Pass Card + Typography (transparent -- background shows through) */}
      <div className="hidden lg:flex w-1/2 relative overflow-hidden">

        <div className="relative z-10 flex flex-col items-center justify-center w-full px-12 xl:px-20 py-16">
          {/* Step title - compact above card */}
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              className="text-center mb-10"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            >
              <h1 className="text-[clamp(2.5rem,6vw,5rem)] font-extralight tracking-[-0.04em] leading-[0.9] text-white mb-3">
                {currentTitle.main}
              </h1>
              <p className="text-base font-light text-white/35 tracking-wide max-w-sm mx-auto">
                {currentTitle.sub}
              </p>
            </motion.div>
          </AnimatePresence>

          {/* Frameless Identity Pass - floating, outline only, screen-like */}
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="relative flex flex-col items-center"
          >
            {/* Ambient glow behind card */}
            <div
              className="absolute w-[340px] h-[440px] rounded-full blur-[100px] opacity-12 pointer-events-none"
              style={{ background: 'radial-gradient(ellipse at center, #14b8a6 0%, #6366f1 50%, transparent 100%)' }}
            />

            {/* Subtle float animation */}
            <motion.div
              animate={prefersReducedMotion ? {} : {
                y: [-4, 4],
                rotateZ: [-0.5, 0.5],
              }}
              transition={{
                y: { duration: 6, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut' },
                rotateZ: { duration: 8, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut' },
              }}
            >
              {/* The Pass Card - frameless, outline only */}
              <div
                className="relative w-[260px] xl:w-[290px] rounded-[22px] overflow-hidden"
                style={{
                  aspectRatio: '0.63',
                  background: 'transparent',
                  boxShadow: `
                    0 30px 60px -15px rgba(0, 0, 0, 0.5),
                    0 0 80px -10px rgba(20, 184, 166, 0.08)
                  `,
                }}
              >
                {/* Outline border - thin, luminous */}
                <div
                  className="absolute inset-0 rounded-[22px] pointer-events-none"
                  style={{
                    border: '1px solid rgba(255, 255, 255, 0.12)',
                    background: 'linear-gradient(145deg, rgba(15, 15, 35, 0.6) 0%, rgba(10, 10, 25, 0.8) 100%)',
                    backdropFilter: 'blur(60px) saturate(180%)',
                    WebkitBackdropFilter: 'blur(60px) saturate(180%)',
                  }}
                />

                {/* Holographic shimmer */}
                <motion.div
                  className="absolute inset-0 rounded-[22px] pointer-events-none opacity-30"
                  animate={prefersReducedMotion ? {} : {
                    background: [
                      'linear-gradient(135deg, transparent 0%, rgba(20,184,166,0.2) 25%, rgba(168,85,247,0.15) 50%, rgba(99,102,241,0.2) 75%, transparent 100%)',
                      'linear-gradient(225deg, transparent 0%, rgba(99,102,241,0.2) 25%, rgba(20,184,166,0.15) 50%, rgba(168,85,247,0.2) 75%, transparent 100%)',
                    ],
                  }}
                  transition={{ duration: 5, repeat: Infinity, ease: 'linear' }}
                  style={{ mixBlendMode: 'overlay' }}
                />

                {/* Header Strip */}
                <div className="relative h-[60px] xl:h-[66px] overflow-hidden rounded-t-[22px]">
                  <div
                    className="absolute inset-0"
                    style={{ background: 'linear-gradient(135deg, rgba(20,184,166,0.8) 0%, rgba(13,148,136,0.7) 30%, rgba(8,145,178,0.6) 60%, rgba(14,116,144,0.5) 100%)' }}
                  />
                  <motion.div
                    className="absolute inset-0"
                    style={{
                      background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.2) 50%, transparent 100%)',
                      backgroundSize: '200% 100%',
                    }}
                    animate={prefersReducedMotion ? {} : { backgroundPosition: ['200% 0', '-200% 0'] }}
                    transition={{ duration: 3.5, repeat: Infinity, ease: 'linear' }}
                  />
                  <div className="absolute top-3 left-4 flex items-center gap-2 z-10">
                    <div className="w-7 h-7 rounded-lg bg-white/15 backdrop-blur-sm flex items-center justify-center border border-white/25">
                      <span className="text-sm font-extralight text-white">Λ</span>
                    </div>
                    <div>
                      <div className="text-[7px] text-white/70 uppercase tracking-[0.15em]">LUKHAS</div>
                      <div className="text-[10px] font-semibold text-white">Identity Pass</div>
                    </div>
                  </div>
                  <motion.div
                    className="absolute top-3.5 right-4 z-10"
                    animate={prefersReducedMotion ? {} : { opacity: [0.4, 0.8, 0.4] }}
                    transition={{ duration: 2.5, repeat: Infinity }}
                  >
                    <svg className="w-4 h-4 text-white/60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M6 8.5a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v7a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4v-7Z"/>
                      <path d="M15 8.5v7c0 .83-.67 1.5-1.5 1.5h-3c-.83 0-1.5-.67-1.5-1.5v-7c0-.83.67-1.5 1.5-1.5h3c.83 0 1.5.67 1.5 1.5Z"/>
                    </svg>
                  </motion.div>
                </div>

                {/* Pass Content */}
                <div className="relative p-4 flex flex-col" style={{ height: 'calc(100% - 60px)' }}>
                  <div className="mb-2">
                    <div className="text-[7px] text-white/35 uppercase tracking-widest mb-1">Identity</div>
                    <div className="text-xl xl:text-2xl font-light text-white/90 tracking-wide" style={brandFontStyle}>
                      ΛiD-••••-YOU
                    </div>
                  </div>

                  <div className="flex gap-4 mb-3">
                    <div>
                      <div className="text-[6px] text-white/30 uppercase tracking-widest">Status</div>
                      <div className="flex items-center gap-1">
                        <motion.div
                          className="w-1.5 h-1.5 rounded-full bg-emerald-400"
                          animate={prefersReducedMotion ? {} : { opacity: [1, 0.4, 1] }}
                          transition={{ duration: 2, repeat: Infinity }}
                        />
                        <span className="text-[9px] text-emerald-400 font-medium">Verified</span>
                      </div>
                    </div>
                    <div>
                      <div className="text-[6px] text-white/30 uppercase tracking-widest">Tier</div>
                      <div className="text-[9px] text-white/80 font-medium">Personal</div>
                    </div>
                  </div>

                  <div className="flex gap-1 mb-2">
                    {['Passkey', 'NFC', 'ZKP'].map((cap) => (
                      <div key={cap} className="px-1.5 py-0.5 rounded-full bg-white/[0.04] border border-white/[0.08] text-[6px] text-white/45">
                        {cap}
                      </div>
                    ))}
                  </div>

                  <div className="flex-1" />

                  {/* Thin separator line */}
                  <div className="h-px bg-gradient-to-r from-transparent via-white/8 to-transparent mb-3" />

                  <div className="flex items-end justify-between">
                    <div className="bg-white/90 p-0.5 rounded">
                      <svg className="w-9 h-9 xl:w-10 xl:h-10" viewBox="0 0 48 48">
                        <rect fill="#0f0f23" width="48" height="48" rx="2"/>
                        <rect fill="#fff" x="4" y="4" width="12" height="12"/>
                        <rect fill="#0f0f23" x="6" y="6" width="8" height="8"/>
                        <rect fill="#fff" x="8" y="8" width="4" height="4"/>
                        <rect fill="#fff" x="32" y="4" width="12" height="12"/>
                        <rect fill="#0f0f23" x="34" y="6" width="8" height="8"/>
                        <rect fill="#fff" x="36" y="8" width="4" height="4"/>
                        <rect fill="#fff" x="4" y="32" width="12" height="12"/>
                        <rect fill="#0f0f23" x="6" y="34" width="8" height="8"/>
                        <rect fill="#fff" x="8" y="36" width="4" height="4"/>
                        <rect fill="#fff" x="20" y="4" width="4" height="4"/>
                        <rect fill="#fff" x="20" y="12" width="4" height="4"/>
                        <rect fill="#fff" x="4" y="20" width="4" height="4"/>
                      </svg>
                    </div>
                    <div className="text-right">
                      <div className="text-[7px] text-white/25 uppercase tracking-widest">lukhas.id</div>
                      <div className="text-[9px] text-white/45">@you</div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Floating Lambda symbol */}
        <motion.div
          className="absolute top-8 left-12 xl:left-20"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <span className="text-3xl font-extralight tracking-[0.2em] text-teal-400/20">
            Λ
          </span>
        </motion.div>

        {/* Progress bar */}
        <div className="absolute bottom-10 left-12 xl:left-20 right-12 xl:right-20">
          <div className="flex items-center gap-6">
            <span className="text-xs font-light tracking-[0.3em] text-teal-400/40 uppercase">
              {String(step).padStart(2, '0')} / 05
            </span>
            <div className="flex-1 h-px bg-white/10 relative">
              <motion.div
                className="absolute left-0 top-0 h-full bg-gradient-to-r from-teal-500 to-cyan-500"
                initial={{ width: 0 }}
                animate={{ width: `${(step / 5) * 100}%` }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              />
            </div>
          </div>
        </div>

        {/* Vertical accent line */}
        <div className="absolute right-0 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-teal-400/10 to-transparent" />
      </div>

      {/* Right Panel - Form Interface (frosted glass over background) */}
      <div
        className="flex-1 lg:w-1/2 flex flex-col border-l border-white/5"
        style={{
          background: 'rgba(5, 5, 18, 0.75)',
          backdropFilter: 'blur(40px) saturate(150%)',
          WebkitBackdropFilter: 'blur(40px) saturate(150%)',
        }}
      >
        {/* Mobile header */}
        <div className="lg:hidden p-6 border-b border-white/5">
          <div className="flex items-center justify-between">
            <span className="text-2xl font-extralight tracking-[0.2em] text-teal-400/50">
              Λ
            </span>
            <span className="text-xs font-light tracking-[0.3em] text-teal-400/40 uppercase">
              {String(step).padStart(2, '0')} / 05
            </span>
          </div>
        </div>

        {/* Form content */}
        <div className="flex-1 flex items-center justify-center p-8 lg:p-16">
          <div className="w-full max-w-md">
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              >
                {step === 1 && <WelcomeStep onNext={handleNextStep} />}
                {step === 2 && <VerifyEmailStep onNext={handleNextStep} />}
                {step === 3 && <SecureIdentityStep onNext={handleNextStep} />}
                {step === 4 && <NamespaceEducation onNext={handleNextStep} />}
                {step === 5 && <CompletionStep />}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-white/5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6 text-xs font-light text-white/20">
              <a href="/privacy" className="hover:text-white/40 transition-colors duration-300">
                Privacy
              </a>
              <a href="/terms" className="hover:text-white/40 transition-colors duration-300">
                Terms
              </a>
            </div>
            <a
              href="/"
              className="text-xs font-light text-white/20 hover:text-white/40 transition-colors duration-300"
            >
              Back
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}
